import { AppAdminFormLabel, AppButton, AppCard, AppMultiSelect } from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import { SearchTextFieldWithLabel } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { convGtJDate } from '@/utils/dateConverter';
import { convertSeirekiWrap } from '@/utils/wareki';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React from 'react';

type TenantOption = { name: string; site_type: string };

export type ClinicLocationsFilterState = {
  siteTypes: string[];
  locationName: string;
  availableDate: Dayjs | null;
};

export type ClinicLocationsFilterPanelProps = {
  tenants: TenantOption[];
  filters: ClinicLocationsFilterState;
  onChangeFilters: (next: Partial<ClinicLocationsFilterState>) => void;
  onSearch: () => void;
};

const ClinicLocationsFilterPanel: React.FC<ClinicLocationsFilterPanelProps> = ({
  tenants,
  filters,
  onChangeFilters,
  onSearch,
}) => {
  const { colors } = useTheme();
  const canUseSiteTypeFeatures = tenants.length > 1;
  const tenantSiteTypes = tenants.map(t => t.site_type).filter(Boolean);
  const hasAllSiteTypesSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => filters.siteTypes.includes(st));

  return (
    <AppCard className="mb-4">
      <div className="pb-4">
        <div className={`grid gap-4 ${canUseSiteTypeFeatures ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {canUseSiteTypeFeatures && (
            <div>
              <AppAdminFormLabel label="サイト区分" />
              <AppMultiSelect
                id="clinic-locations-site_types"
                placeholder="サイト区分"
                value={filters.siteTypes}
                style={{ width: '100%' }}
                maxTagCount={hasAllSiteTypesSelected ? 0 : 'responsive'}
                maxTagPlaceholder={hasAllSiteTypesSelected ? () => 'すべて' : undefined}
                showSearch
                onInputKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                options={[
                  { label: 'すべて', value: 'all' },
                  ...tenants.map(t => ({ label: t.name, value: t.site_type })),
                ]}
                onChange={values => {
                  const next = (values as string[]) || [];
                  if (next.includes('all')) {
                    onChangeFilters({ siteTypes: tenantSiteTypes });
                    return;
                  }
                  onChangeFilters({ siteTypes: next.filter(v => v !== 'all') });
                }}
              />
            </div>
          )}
          <SearchTextFieldWithLabel
            label="拠点名"
            placeholder="拠点名"
            id="clinic-locations-location_name"
            value={filters.locationName}
            onChange={e => onChangeFilters({ locationName: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onSearch();
              }
            }}
          />
          <div>
            <AppAdminFormLabel label="基準日" />
            <RA2NDatePicker
              className="w-full"
              value={filters.availableDate ? convGtJDate(filters.availableDate) : ''}
              onChange={warekiStr => {
                if (!warekiStr?.trim()) {
                  onChangeFilters({ availableDate: null });
                  return;
                }
                const seireki = convertSeirekiWrap(warekiStr);
                const d = dayjs(seireki.replace(/\//g, '-'));
                onChangeFilters({ availableDate: d.isValid() ? d : null });
              }}
              placeholder="基準日"
              onEnter={onSearch}
            />
          </div>
          <div className="flex items-center">
            <AppButton
              type="primary"
              onClick={onSearch}
              icon={<Search />}
              style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            >
              検索
            </AppButton>
          </div>
        </div>
      </div>
    </AppCard>
  );
};

export default ClinicLocationsFilterPanel;
